import axios, {AxiosPromise} from 'axios'
import {envVars} from '@/envVars.ts'
import {useAuthStore} from '@features/user/store/store.ts'
import {
    LOCAL_STORAGE_ACCESS_TOKEN,
    LOCAL_STORAGE_ADMIN_TOKEN,
    LOCAL_STORAGE_REFRESH_TOKEN
} from '@features/user/constants.ts'
import {z, ZodError, ZodSchema} from 'zod'

declare module 'axios' {
    export interface AxiosRequestConfig {
        skipAuthHeader?: boolean
    }
}

const axiosConfig = {
    baseURL: envVars.VITE_API_ENDPOINT,
    headers: {
        common: {
            Authorization: localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)
                ? `Bearer ${localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)}`
                : false,
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        },
        post: {
            'Access-Control-Allow-Origin': '*'
        }
    }
}

// create axios custom instance with custom config
const axiosInstance = axios.create(axiosConfig)

export const attemptRefresh = async (refreshToken: string) => {
    const url = '/host-services/refresh'
    try {
        const {
            data: {oauth2_authorization}
        } = await axiosInstance.post(url, {refresh_token: refreshToken})
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, oauth2_authorization.access_token)
        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, oauth2_authorization.refresh_token)
        useAuthStore.setState(() => ({
            accessToken: oauth2_authorization.access_token,
            refreshToken: oauth2_authorization.refresh_token
        }))

        return {oauth2_authorization}
    } catch (error) {
        if (axios.isAxiosError(error)) {
            useAuthStore.getState().resetAuthTokens()
        } else {
            console.error(error)
        }
    }
}

axiosInstance.interceptors.request.use(request => {
    const skipAuthHeader = request?.skipAuthHeader ?? false
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)
    const adminAccessToken = localStorage.getItem(LOCAL_STORAGE_ADMIN_TOKEN)

    if (token && !skipAuthHeader) {
        request.headers['Authorization'] = `Bearer ${token}`
    }
    if (adminAccessToken && skipAuthHeader) {
        request.headers['Authorization'] = `Bearer ${adminAccessToken}`
    }
    return request
})

const endpointToIgnore = ['/login']

axiosInstance.interceptors.response.use(
    response => {
        // pass response without change
        return response
    },
    async error => {
        // get error info
        const statusCode = error?.response?.status
        const originalRequest = error.config
        console.log('error', error)
        switch (statusCode) {
            case 401: {
                const refreshToken = useAuthStore.getState().refreshToken
                localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
                if (refreshToken && !originalRequest._retry && !endpointToIgnore.includes(error.config.url)) {
                    originalRequest._retry = true // prevent infinite retry loop
                    await attemptRefresh(refreshToken)
                    return axiosInstance.request(originalRequest)
                } else {
                    useAuthStore.getState().resetAuthTokens()
                    return Promise.reject(error)
                }
            }
            default:
                return Promise.reject(error)
        }
    }
)

export default axiosInstance

/**
 * This function parses the api response using the provided shape and returns the response data or the ZodError if the parse fails
 * */
export const parseAxiosPromise = <T extends ZodSchema>({
    axiosPromise,
    responseShape,
    onZodError
}: {
    axiosPromise: AxiosPromise
    responseShape: T
    onZodError?: (error: ZodError) => void
}): Promise<z.infer<T>> =>
    axiosPromise.then(response => {
        const safeParsedResponseData = responseShape.safeParse(response.data)
        if (!safeParsedResponseData.success) {
            console.error(safeParsedResponseData.error.message)
            onZodError?.(safeParsedResponseData.error)
            throw new Error(safeParsedResponseData.error.message)
        }

        return response.data
    })
